import React, { useCallback, useState } from 'react';
import { CheckCircle2, Clock, Cpu, Timer, TrendingUp } from 'lucide-react';

import { loadAnalytics } from './api';
import {
  Bars, Card, DayChart, Empty, Failed, Head, Loading, Metric, Metrics,
  Segmented, SplitBar, duration,
} from './parts';
import { useLiveResource } from './useLive';
import { tx } from './i18n';

// What the cluster has actually done, over a window you choose.
//
// Everything is measured from xomics_jobs on request rather than kept in a
// counter, so nothing here can disagree with the rows it describes. Without
// ANALYSIS_READ_ALL the server scopes it to your own work, which is why the
// heading says whose numbers these are rather than leaving it ambiguous.
export default function XomicsAnalytics() {
  const [days, setDays] = useState(30);
  const load = useCallback(() => loadAnalytics(days), [days]);
  const report = useLiveResource(['jobs'], load);
  const data = report.data;

  const window = (
    <Segmented
      value={days}
      onChange={setDays}
      options={[
        { value: 7, label: tx("ui.analytics.7_days_0a8dd6ad") },
        { value: 30, label: tx("ui.analytics.30_days_7d4278a8") },
        { value: 90, label: tx("ui.analytics.90_days_170621ac") },
      ]}
    />
  );

  if (report.loading && !data) return <><Head title={tx("ui.analytics.analytics_25bc9629")} />{<Loading label={tx("ui.analytics.measuring_8945ea3a")} />}</>;
  if (report.error) return <Failed message={report.error} onRetry={report.refresh} />;
  if (!data) return null;

  const { totals } = data;
  const finished = totals.complete + totals.failed;
  const successRate = finished > 0 ? Math.round((totals.complete / finished) * 100) : null;
  const busiest = data.series.reduce((top, day) => (day.submitted > (top?.submitted ?? -1) ? day : top), null);

  return (
    <>
      <Head
        title={tx("ui.analytics.analytics_25bc9629")}
        subtitle={data.scope === 'mine'
          ? tx("ui.analytics.analyses_you_submitted_in_the_last_days_days_24e0d40b", { days: data.days })
          : tx("ui.analytics.every_analysis_in_the_last_days_days_fcec40fb", { days: data.days })}
      >
        {window}
      </Head>

      {totals.runs === 0 ? (
        <Card>
          <Empty
            title={tx("ui.analytics.nothing_ran_in_this_window_258ca6b4")}
            detail={tx("ui.analytics.widen_the_range_or_submit_an_upload_and_it_will_appear_5566b645")}
          />
        </Card>
      ) : (
        <>
          <Metrics>
            <Metric
              icon={TrendingUp} tone="info" label={tx("ui.analytics.submitted_2e00359b")} value={totals.runs}
              note={busiest ? tx("ui.analytics.busiest_day_submitted_on_value_4f775cdb", { submitted: busiest.submitted, value: new Date(busiest.at).toLocaleDateString() }) : undefined}
            />
            <Metric
              icon={CheckCircle2} tone={successRate !== null && successRate >= 95 ? 'ok' : 'warn'}
              label={tx("ui.analytics.succeeded_c8fbda90")} value={successRate === null ? 0 : successRate} unit="%"
              note={tx("ui.analytics.complete_of_finished_finished_b3965a9a", { complete: totals.complete, finished: finished })}
              noteTone={successRate !== null && successRate >= 95 ? 'ok' : 'warn'}
            />
            <Metric
              icon={Timer} tone="flat" label={tx("ui.analytics.median_run_02b7a4ee")}
              value={duration(totals.avgRunMs)}
              note={tx("ui.analytics.from_start_to_results_offloaded_f11be4ce")}
            />
            <Metric
              icon={Clock} tone={totals.avgWaitMs > 600000 ? 'warn' : 'flat'}
              label={tx("ui.analytics.queue_wait_49207c14")} value={duration(totals.avgWaitMs)}
              note={totals.avgWaitMs > 600000 ? tx("ui.analytics.work_is_waiting_for_a_free_worker_6dd09dcb") : tx("ui.analytics.time_before_a_worker_picked_it_up_2b695e7b")}
              noteTone={totals.avgWaitMs > 600000 ? 'warn' : undefined}
            />
            <Metric
              icon={Cpu} tone="flat" label={tx("ui.analytics.compute_used_65cd20fc")}
              value={duration(totals.computeMs)}
              note={tx("ui.analytics.total_wall_time_across_all_runs_d502a813")}
            />
          </Metrics>

          <Card
            title={tx("ui.analytics.submissions_per_day_0650518d")}
            subtitle={tx("ui.analytics.failures_stack_at_the_base_of_each_bar_771ebba8")}
            footer={<span>{data.series.length === 1
              ? tx('ui.analytics.one_day_with_activity', { count: data.series.length })
              : tx('ui.analytics.many_days_with_activity', { count: data.series.length })}</span>}
          >
            <DayChart series={data.series} />
          </Card>

          <div className="xo-two">
            <Card
              title={tx("ui.analytics.by_pipeline_1018584f")}
              subtitle={tx("ui.analytics.runs_in_this_window_and_how_long_each_actually_took_534946df")}
            >
              <Bars
                rows={data.pipelines.map((pipeline) => ({
                  label: pipeline.label,
                  // The measured figure against what the pipeline claims, so a
                  // bad estimate is visible here rather than only in a timeout.
                  detail: pipeline.avgRunMs !== null
                    ? tx('ui.analytics.pipeline_runtime_with_estimate', {
                      version: pipeline.codeVersion,
                      average: duration(pipeline.avgRunMs),
                      estimate: pipeline.estimateMinutes,
                    })
                    : tx('ui.analytics.pipeline_runtime_without_estimate', {
                      version: pipeline.codeVersion,
                      average: duration(pipeline.avgRunMs),
                    }),
                  value: pipeline.runs,
                  tone: pipeline.failed > 0 ? 'warn' : 'info',
                }))}
              />
            </Card>

            <Card title={tx("ui.analytics.outcomes_6848ce55")} subtitle={tx("ui.analytics.every_run_in_the_window_b477402d")}>
              <SplitBar
                parts={[
                  { label: tx("ui.analytics.complete_1f5a1abf"), value: totals.complete, tone: 'ok' },
                  { label: tx("ui.analytics.failed_09fef5d8"), value: totals.failed, tone: 'bad' },
                  { label: tx("ui.analytics.running_73989d9c"), value: totals.running, tone: 'info' },
                  { label: tx("ui.analytics.queued_6a599877"), value: totals.pending, tone: 'warn' },
                ]}
              />

              {data.sampleTypes.length > 0 && (
                <div style={{ marginTop: 22 }}>
                  <p className="xo-eyebrow" style={{ marginBottom: 10 }}>{tx("ui.analytics.by_sample_type_db594572")}</p>
                  <Bars
                    rows={data.sampleTypes.map((sample) => ({
                      label: sample.code, value: sample.runs, tone: 'flat',
                    }))}
                  />
                </div>
              )}
            </Card>
          </div>

          {data.pipelines.some((pipeline) => pipeline.failed > 0) && (
            <Card
              title={tx("ui.analytics.where_failures_are_bcdc8740")}
              subtitle={tx("ui.analytics.only_pipelines_that_failed_at_least_once_57af4258")}
              footer={<span>{tx("ui.analytics.a_pipeline_failing_repeatedly_is_worth_a_look_before_t_a45dace6")}</span>}
            >
              <Bars
                tone="bad"
                rows={data.pipelines
                  .filter((pipeline) => pipeline.failed > 0)
                  .map((pipeline) => ({
                    label: pipeline.label,
                    detail: tx("ui.analytics.failed_of_runs_runs_fa616de2", { failed: pipeline.failed, runs: pipeline.runs }),
                    value: pipeline.failed,
                    display: `${Math.round((pipeline.failed / pipeline.runs) * 100)}%`,
                    tone: 'bad',
                  }))}
              />
            </Card>
          )}
        </>
      )}
    </>
  );
}
